import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Team } from '@rundown-studio/types'
import type {
  CreateTeamBody,
  GetTeamsByUserIdParams,
  PatchTeamMemberParams,
  GetTeamParams,
  GetTeamMembersMetadataParams,
  InviteTeamMemberParams,
  RemoveTeamMemberParams,
  ResendInviteParams,
  GetTeamPlanParams,
} from './params.ts'
import {
  createTeam,
  getTeamsByUserId,
  patchTeamMember,
  getTeam,
  getTeamMembersMetadata,
  inviteTeamMember,
  removeTeamMember,
  resendInvite,
  getTeamPlan,
} from './requests.ts'
import { useAtom, useSetAtom } from 'jotai'
import { userTeamsAtom, userTeamAtom } from '../../../store/user.store'
import type { MutationHook, QueryHook } from '@/lib/react-query'
import { createQueryKeys } from '@lukemorales/query-key-factory'
import type { TeamMembersMetadataDto } from '../model/dtos'

export const teamQueryKeys = createQueryKeys('TEAM', {
  byId: ({ teamId, ...params }: GetTeamParams) => [teamId, params],
  getTeamsByUserId: (params: GetTeamsByUserIdParams) => [params],
  teamMembersMetadata: ({
    teamId,
    ...params
  }: GetTeamMembersMetadataParams) => [teamId, params],
  plan: (params: GetTeamPlanParams) => [params],
})

export const useCreateTeam = ({
  options,
}: MutationHook<Team, CreateTeamBody> = {}) => {
  const [teams, setTeams] = useAtom(userTeamsAtom)
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (body) => {
      const { data } = await createTeam(body)

      return data
    },
    onSuccess(newTeam, { uid }) {
      const newUserTeamsData = teams ? [...teams, newTeam] : [newTeam]

      setTeams(newUserTeamsData)
      queryClient.setQueryData(
        teamQueryKeys.getTeamsByUserId({
          userId: uid,
        }).queryKey,
        newUserTeamsData,
      )
    },
  })
}

export const useTeamsByUserId = ({
  params,
  options,
}: QueryHook<Team[], GetTeamsByUserIdParams>) => {
  const setUserTeams = useSetAtom(userTeamsAtom)

  return useQuery({
    queryKey: teamQueryKeys.getTeamsByUserId(params).queryKey,
    queryFn: async () => {
      const { data } = await getTeamsByUserId(params)

      setUserTeams(data)

      return data
    },
    ...options,
  })
}

export const useUpdateTeamMember = ({
  options,
}: MutationHook<Team, PatchTeamMemberParams> = {}) => {
  const queryClient = useQueryClient()
  const setTeam = useSetAtom(userTeamAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await patchTeamMember(params)

      return data
    },
    onSuccess(team) {
      queryClient.setQueryData(
        teamQueryKeys.byId({ teamId: team.id }).queryKey,
        team,
      )
      setTeam(team)
    },
  })
}

export const useTeam = ({
  params,
  options,
  hideToast,
}: QueryHook<Team, GetTeamParams>) => {
  return useQuery({
    ...options,
    queryKey: teamQueryKeys.byId(params).queryKey,
    queryFn: async () => {
      const { data } = await getTeam(params, { toast: !hideToast })

      return data
    },
  })
}

export const useTeamMembersMetadata = ({
  params,
  options,
}: QueryHook<TeamMembersMetadataDto, GetTeamMembersMetadataParams>) => {
  return useQuery({
    ...options,
    queryKey: teamQueryKeys.teamMembersMetadata(params).queryKey,
    queryFn: async () => {
      const { data } = await getTeamMembersMetadata(params)

      return data
    },
  })
}

export const useTeamPlan = ({
  params,
  options,
  hideToast,
}: QueryHook<unknown, GetTeamPlanParams>) => {
  return useQuery({
    ...options,
    queryKey: teamQueryKeys.plan(params).queryKey,
    queryFn: async () => {
      const { data } = await getTeamPlan(params, { toast: !hideToast })

      return data
    },
  })
}

export const useInviteTeamMember = ({
  options,
}: MutationHook<Team, InviteTeamMemberParams> = {}) => {
  const queryClient = useQueryClient()
  const setActiveTeam = useSetAtom(userTeamAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await inviteTeamMember(params)

      return data
    },
    async onSuccess(team) {
      await queryClient.invalidateQueries({
        queryKey: teamQueryKeys.teamMembersMetadata({ teamId: team.id })
          .queryKey,
      })
      queryClient.setQueryData(
        teamQueryKeys.byId({ teamId: team.id }).queryKey,
        team,
      )
      setActiveTeam(team)
    },
  })
}

export const useRemoveTeamMember = ({
  options,
}: MutationHook<Team, RemoveTeamMemberParams> = {}) => {
  const queryClient = useQueryClient()
  const setActiveTeam = useSetAtom(userTeamAtom)

  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await removeTeamMember(params)

      return data
    },
    onSuccess(team) {
      queryClient.setQueryData(
        teamQueryKeys.byId({ teamId: team.id }).queryKey,
        team,
      )
      setActiveTeam(team)
    },
  })
}

export const useResendInvite = ({
  options,
}: MutationHook<string, ResendInviteParams> = {}) => {
  return useMutation({
    ...options,
    mutationFn: async (params) => {
      const { data } = await resendInvite(params)

      return data
    },
  })
}
